import { buildLogger } from '@instinet-analytics/core-lib';
import { selectActiveWidgetIds } from 'layout/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import without from 'lodash/without';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService';
import { WidgetCustomStreamManagerSupportingRegisteredSymbolsBase } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManagerSupportingRegisteredSymbols';
import { WidgetCustomManagedStreamOptions } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { getOrderVolumeProfileMessageHandler } from './orderVolumeProfileMessageHandler.ts';
import { OrderVolumeProfileStreamRequest } from '../../api/OrderVolumeProfileStreamRequest.ts';
import { selectOrderVolumeProfileState } from '../selectors.ts';
import {
    buildOrderVolumeProfileRequest,
    orderVolumeProfileRequestToStreamKey,
} from './request-utils';
import { OrderVolumeProfileElement } from '../../api/OrderVolumeProfileElement.ts';
import { WidgetId } from 'widgets/WidgetId.ts';

type RequestLookup = { [key: string]: OrderVolumeProfileStreamRequest };
/*
 * Custom manager for the OrderVolumeProfile stream
 */
export class OrderVolumeStreamManager extends WidgetCustomStreamManagerSupportingRegisteredSymbolsBase {
    private readonly streamDataService: StreamDataService;
    private readonly messageHandlers: StreamDataHandlers<OrderVolumeProfileElement>;

    private requests: RequestLookup = {};
    private stoppedRequests: RequestLookup = {};

    public constructor(options: WidgetCustomManagedStreamOptions) {
        super(
            options.store,
            buildLogger(
                'OrderVolumeProfileStreamManager',
                options.loggingService
            )
        );
        this.streamDataService = options.streamDataService;
        this.messageHandlers = getOrderVolumeProfileMessageHandler(
            this.store,
            this.logger
        );
    }

    public resetState() {
        this.requests = {};
    }

    public checkStream(reason: string, dispatcher: ActionsDispatcher): void {
        this.logger.debug('Checking stream', reason);

        this.getActiveRequestsFromState().then((nextRequests) => {
            const nextKeys = Object.keys(nextRequests);
            const currentKeys = Object.keys(this.requests);

            const removing = without(currentKeys, ...nextKeys);

            for (const removedRequestKey of removing) {
                this.stopStreaming(removedRequestKey, dispatcher, reason);
                this.stoppedRequests[removedRequestKey] =
                    this.requests[removedRequestKey];
            }

            this.requests = nextRequests;

            for (const nextKey of nextKeys) {
                const isPreviouslyStoppedStream =
                    !!this.stoppedRequests[nextKey];
                const isNewStream = currentKeys.indexOf(nextKey) === -1;

                if (isPreviouslyStoppedStream) {
                    delete this.stoppedRequests[nextKey];
                }

                if (isPreviouslyStoppedStream || isNewStream) {
                    this.startOrUpdateStream(nextKey, dispatcher, reason);
                }
            }
        });
    }

    public getInfo(): Record<string, unknown> {
        return {
            type: 'OrderVolumeProfileStreamManager',
            registeredSymbolStreams: this.registeredSymbolStreams,
            widgetIds: this.widgetIds,
            requests: this.requests,
        };
    }

    protected startOrUpdateStream(
        streamKey: string,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        this.logger.debug('Starting/updating stream', {
            streamKey,
            reason,
        });

        const state = this.store.getState();

        const currentChannelId = selectChannelId(state, streamKey);

        (async () => {
            await this.streamDataService.startAndWatchStream(
                streamKey,
                'startOrderVolumeCurveStream',
                this.messageHandlers,
                currentChannelId,
                dispatcher
            )(this.requests[streamKey]);
        })();
    }

    protected stopStreaming(
        streamKey: string,
        dispatcher: ActionsDispatcher,
        reason: string
    ) {
        const stopChannelDestination = 'stopOrderVolumeProfileStream';

        this.logger.debug('Stopping streaming ' + stopChannelDestination, {
            reason,
            streamKey,
        });

        // Stop the stream but we keep the channel listener and data to ensure we don't miss messages (as we reuse the channel)...
        this.streamDataService.stopStream(streamKey, dispatcher, true);
    }

    private async getActiveRequestsFromState(): Promise<RequestLookup> {
        const state = this.store.getState();
        const activeWidgetIds = selectActiveWidgetIds(state);

        const activeRegisteredWidgetIds = this.widgetIds.filter(
            (a) => activeWidgetIds.indexOf(a.widgetKey) > -1
        );

        const selection = selectPortfolioSelection(state);
        if (!selection) {
            return {};
        }

        const { selectedSymbol, selectedOrder, symbology } =
            new PortfolioSelectionWrapper(selection);

        if (!selectedOrder) {
            return {};
        }

        const requests: RequestLookup = {};

        for (const widgetId of activeRegisteredWidgetIds) {
            const widgetState = selectOrderVolumeProfileState(
                state,
                widgetId.widgetKey
            );
            const request = buildOrderVolumeProfileRequest(
                selectedSymbol!,
                symbology,
                selectedOrder,
                widgetState?.config,
                widgetState?.component
            );
            const key = orderVolumeProfileRequestToStreamKey(request);
            requests[key] = request;
        }

        this.logger.debug('Registered widget ids are', {
            activeRegisteredWidgetIds,
            streamKeys: Object.keys(requests),
        });

        return requests;
    }

    public override onWidgetComponentStateChanged(
        _widgetId: WidgetId,
        dispatcher: ActionsDispatcher
    ) {
        this.checkStream('component state changed', dispatcher);
    }

    public override onWidgetConfigChanged(
        _widgetId: WidgetId,
        dispatcher: ActionsDispatcher
    ) {
        this.checkStream('config state changed', dispatcher);
    }
}
